import {
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock,
  ExternalLink,
  Loader2,
  Plus,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import type { CoachingHomeworkWithProblem } from '../problems/types'
import { useStandaloneTasks } from './hooks/use-standalone-tasks'

interface StandaloneTasksWidgetProps {
  assignedHomework: CoachingHomeworkWithProblem[]
  onAssignTask: (taskId: string, title: string) => Promise<void>
}

export function StandaloneTasksWidget({
  assignedHomework,
  onAssignTask,
}: StandaloneTasksWidgetProps) {
  const { tasks, loading, error } = useStandaloneTasks()
  const [isExpanded, setIsExpanded] = useState(false)
  const [addingTaskId, setAddingTaskId] = useState<string | null>(null)

  // Get set of already assigned task IDs
  const assignedTaskIds = useMemo(() => {
    return new Set(
      assignedHomework
        .filter((h) => h.status === 'active' && h.taskId)
        .map((h) => h.taskId as string),
    )
  }, [assignedHomework])

  // Calculate stats
  const stats = useMemo(() => {
    const total = tasks.length
    const assigned = tasks.filter((t) => assignedTaskIds.has(t.id)).length
    return { total, assigned, unassigned: total - assigned }
  }, [tasks, assignedTaskIds])

  const handleAddTask = async (taskId: string, title: string) => {
    if (assignedTaskIds.has(taskId)) return

    setAddingTaskId(taskId)
    try {
      await onAssignTask(taskId, title)
      toast.success(`Added "${title}"`)
    } catch (err) {
      console.error('Error adding task:', err)
      toast.error('Failed to add task')
    } finally {
      setAddingTaskId(null)
    }
  }

  if (loading) {
    return (
      <div className="bg-card border-border overflow-hidden rounded-lg border">
        <div className="flex items-center gap-2 p-4">
          <Loader2 size={18} className="text-muted-foreground animate-spin" />
          <span className="text-muted-foreground text-sm">
            Loading standalone tasks...
          </span>
        </div>
      </div>
    )
  }

  if (error) {
    return null // Silently hide if there's an error
  }

  if (tasks.length === 0) {
    return null // Hide if no standalone tasks available
  }

  return (
    <div className="bg-card border-border overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="border-border hover:bg-muted/50 flex w-full items-center justify-between border-b p-4 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ClipboardList size={18} className="text-muted-foreground" />
          <h2 className="text-foreground text-lg font-semibold">
            Standalone Tasks
          </h2>
          <span className="bg-muted text-muted-foreground ml-2 rounded px-2 py-0.5 text-xs">
            {stats.unassigned}/{stats.total} available
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-muted-foreground" />
        ) : (
          <ChevronDown size={20} className="text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4">
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-muted/30 sticky top-0">
                <tr className="text-muted-foreground text-left text-xs">
                  <th className="w-16 px-3 py-2 font-medium">Action</th>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium">Category</th>
                  <th className="px-3 py-2 font-medium">Est. Time</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => {
                  const isAssigned = assignedTaskIds.has(task.id)
                  const isAdding = addingTaskId === task.id

                  return (
                    <tr
                      key={task.id}
                      className={`border-border border-b transition-colors ${
                        isAssigned
                          ? 'bg-muted/20 opacity-60'
                          : 'hover:bg-muted/30'
                      }`}
                    >
                      <td className="px-3 py-2">
                        {isAssigned ? (
                          <div className="flex items-center gap-1 text-sm text-green-600">
                            <Check size={14} />
                            <span>Added</span>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={() => handleAddTask(task.id, task.title)}
                            disabled={isAdding}
                            aria-label={`Add ${task.title} as homework`}
                          >
                            {isAdding ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Plus size={14} />
                            )}
                          </Button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-foreground text-sm font-medium ${isAssigned ? 'line-through' : ''}`}
                          >
                            {task.title}
                          </span>
                          {task.url && (
                            <a
                              href={task.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-primary"
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Open ${task.title} in new tab`}
                            >
                              <ExternalLink size={12} />
                            </a>
                          )}
                        </div>
                        {task.notes && (
                          <p className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                            {task.notes}
                          </p>
                        )}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-sm">
                        {task.category || '-'}
                      </td>
                      <td className="px-3 py-2">
                        {task.estimatedMinutes ? (
                          <div className="text-muted-foreground flex items-center gap-1 text-sm">
                            <Clock size={12} />
                            <span>{task.estimatedMinutes} min</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            -
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="text-muted-foreground mt-3 text-xs">
            Click the + button to assign a standalone task as homework.
          </p>
        </div>
      )}
    </div>
  )
}
